import { useState, useEffect } from 'react';
import { 
  Settings, Lock, Leaf, FileText, Plus, Trash2, Edit2, 
  Save, X, RefreshCw, AlertTriangle, Database
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [herbs, setHerbs] = useState([]);
  const [needs, setNeeds] = useState([]);
  const [editingHerb, setEditingHerb] = useState(null);
  const [showHerbDialog, setShowHerbDialog] = useState(false);

  useEffect(() => {
    // Check if already authenticated (session)
    const auth = sessionStorage.getItem('acupedia_admin_auth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const handleLogin = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        auth: {
          username: 'admin',
          password: password
        }
      });
      setIsAuthenticated(true);
      sessionStorage.setItem('acupedia_admin_auth', 'true');
      toast.success('Autentificare reușită!');
      fetchData();
    } catch (error) {
      toast.error('Parolă incorectă');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [herbsRes, needsRes] = await Promise.all([
        axios.get(`${API}/herbs`),
        axios.get(`${API}/needs`)
      ]);
      setHerbs(herbsRes.data);
      setNeeds(needsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleSeedDatabase = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/seed`, {}, {
        auth: {
          username: 'admin',
          password: password
        }
      });
      toast.success('Baza de date a fost populată cu succes!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la popularea bazei de date');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('acupedia_admin_auth');
    setPassword('');
    toast.success('Deconectat');
  };

  const handleDeleteHerb = async (herbId) => {
    if (!window.confirm('Sigur dorești să ștergi această plantă?')) return;
    
    try {
      await axios.delete(`${API}/herbs/${herbId}`, {
        auth: {
          username: 'admin',
          password: password
        }
      });
      toast.success('Plantă ștearsă cu succes');
      fetchData();
    } catch (error) {
      toast.error('Eroare la ștergerea plantei');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="py-16 md:py-24 animate-fade-in">
        <div className="container-acupedia max-w-md">
          <div className="card-acupedia p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-acupedia-teal/10 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-8 h-8 text-acupedia-teal" />
            </div>
            <h1 className="font-heading text-2xl text-stone-800 mb-2">Administrare</h1>
            <p className="text-stone-600 mb-6">Introdu parola pentru a accesa panoul de administrare</p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="admin-password" className="sr-only">Parolă</Label>
                <Input
                  id="admin-password"
                  type="password"
                  placeholder="Parolă admin"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  className="input-acupedia"
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                onClick={handleLogin}
                disabled={loading || !password}
                className="btn-primary w-full"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Lock className="w-5 h-5 mr-2" />
                    Autentificare
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-stone-400 mt-6">
              Parola implicită: acupedia2024
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 md:py-12 animate-fade-in">
      <div className="container-acupedia">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-acupedia-teal flex items-center justify-center">
                <Settings className="w-5 h-5 text-white" />
              </div>
              <h1 className="section-header mb-0">Panou de Administrare</h1>
            </div>
            <p className="section-subheader">Gestionează catalogul de plante și nevoi</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleSeedDatabase}
              disabled={loading}
              data-testid="seed-db-btn"
            >
              <Database className="w-4 h-4 mr-2" />
              Resetează date
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </div>

        {/* Admin Tabs */}
        <Tabs defaultValue="herbs" className="admin-panel">
          <div className="admin-header">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="herbs" className="flex items-center gap-2" data-testid="tab-herbs">
                <Leaf className="w-4 h-4" />
                Plante ({herbs.length})
              </TabsTrigger>
              <TabsTrigger value="needs" className="flex items-center gap-2" data-testid="tab-needs">
                <FileText className="w-4 h-4" />
                Nevoi ({needs.length})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Herbs Tab */}
          <TabsContent value="herbs" className="admin-content">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-heading text-xl text-stone-800">Catalogul de Plante</h2>
              <Button
                onClick={() => {
                  setEditingHerb({
                    name_ro: '',
                    name_latin: '',
                    forms: ['uscat'],
                    energetic_temp: 'neutru',
                    meridians: [],
                    safety_flags: {},
                    description: '',
                    dosage_min_g: 1.0,
                    dosage_max_g: 3.0,
                    infusion_time_min: 5,
                    infusion_time_max: 10
                  });
                  setShowHerbDialog(true);
                }}
                className="btn-primary"
                data-testid="add-herb-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adaugă plantă
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-stone-200">
                    <th className="text-left p-3 text-sm font-medium text-stone-600">Nume RO</th>
                    <th className="text-left p-3 text-sm font-medium text-stone-600">Nume Latin</th>
                    <th className="text-left p-3 text-sm font-medium text-stone-600">Temperatură</th>
                    <th className="text-left p-3 text-sm font-medium text-stone-600">Dozaj</th>
                    <th className="text-left p-3 text-sm font-medium text-stone-600">Meridiane</th>
                    <th className="text-right p-3 text-sm font-medium text-stone-600">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {herbs.map((herb) => (
                    <tr key={herb.id} className="border-b border-stone-100 hover:bg-stone-50">
                      <td className="p-3 font-medium text-stone-800">{herb.name_ro}</td>
                      <td className="p-3 text-stone-600 italic text-sm">{herb.name_latin}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          herb.energetic_temp === 'cald' ? 'bg-orange-100 text-orange-800' :
                          herb.energetic_temp === 'rece' ? 'bg-cyan-100 text-cyan-800' :
                          'bg-stone-100 text-stone-700'
                        }`}>
                          {herb.energetic_temp}
                        </span>
                      </td>
                      <td className="p-3 font-mono text-sm text-stone-600">
                        {herb.dosage_min_g}-{herb.dosage_max_g}g
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {herb.meridians?.slice(0, 2).map((m, i) => (
                            <span key={i} className="text-xs bg-acupedia-teal/10 text-acupedia-teal px-2 py-0.5 rounded">
                              {m}
                            </span>
                          ))}
                          {herb.meridians?.length > 2 && (
                            <span className="text-xs text-stone-400">+{herb.meridians.length - 2}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingHerb(herb);
                              setShowHerbDialog(true);
                            }}
                            data-testid={`edit-herb-${herb.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteHerb(herb.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-herb-${herb.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {herbs.length === 0 && (
              <div className="text-center py-12">
                <Leaf className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500">Nu există plante în baza de date</p>
                <Button onClick={handleSeedDatabase} variant="outline" className="mt-4">
                  Populează cu date inițiale
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Needs Tab */}
          <TabsContent value="needs" className="admin-content">
            <div className="mb-6">
              <h2 className="font-heading text-xl text-stone-800">Catalogul de Nevoi</h2>
              <p className="text-sm text-stone-500">Nevoile definesc simptomele și mapează plantele asociate</p>
            </div>

            <div className="grid gap-4">
              {needs.map((need) => (
                <div key={need.id} className="card-acupedia p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading text-lg text-acupedia-teal">{need.name_ro}</h3>
                      <p className="text-sm text-stone-600 mt-1">{need.description}</p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Badge variant="outline" className="text-xs">
                          Plante principale: {need.primary_herbs?.length || 0}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Plante secundare: {need.secondary_herbs?.length || 0}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {need.yin_yang}
                        </Badge>
                      </div>
                    </div>
                    <Badge className="bg-acupedia-teal/10 text-acupedia-teal border-0">
                      {need.icon}
                    </Badge>
                  </div>
                  <div className="mt-3 pt-3 border-t border-stone-100">
                    <p className="text-xs text-stone-500">
                      Meridiane: {need.meridians_targeted?.join(', ') || 'N/A'}
                    </p>
                    <p className="text-xs text-stone-500">
                      Direcție: {need.energetic_direction || 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {needs.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500">Nu există nevoi în baza de date</p>
                <Button onClick={handleSeedDatabase} variant="outline" className="mt-4">
                  Populează cu date inițiale
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Info */}
        <div className="mt-8 p-6 bg-cyan-50 rounded-xl border border-cyan-200">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-heading text-lg text-cyan-800 mb-2">
                Informații pentru Admin
              </h4>
              <ul className="text-sm text-cyan-700 space-y-1">
                <li>• Datele sunt stocate în MongoDB</li>
                <li>• Butonul "Resetează date" va șterge toate datele și va repopula cu setul inițial</li>
                <li>• Modificările afectează imediat generarea de rețete</li>
                <li>• Parola implicită poate fi schimbată din variabila ADMIN_PASSWORD din backend</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Herb Edit Dialog */}
        <Dialog open={showHerbDialog} onOpenChange={setShowHerbDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingHerb?.id ? 'Editează Plantă' : 'Adaugă Plantă Nouă'}
              </DialogTitle>
            </DialogHeader>
            {editingHerb && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nume RO</Label>
                    <Input
                      value={editingHerb.name_ro}
                      onChange={(e) => setEditingHerb({...editingHerb, name_ro: e.target.value})}
                      className="input-acupedia mt-1"
                      data-testid="herb-name-ro-input"
                    />
                  </div>
                  <div>
                    <Label>Nume Latin</Label>
                    <Input
                      value={editingHerb.name_latin}
                      onChange={(e) => setEditingHerb({...editingHerb, name_latin: e.target.value})}
                      className="input-acupedia mt-1"
                      data-testid="herb-name-latin-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Descriere</Label>
                  <Input
                    value={editingHerb.description}
                    onChange={(e) => setEditingHerb({...editingHerb, description: e.target.value})}
                    className="input-acupedia mt-1"
                    data-testid="herb-description-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Temperatură energetică</Label>
                    <select
                      value={editingHerb.energetic_temp}
                      onChange={(e) => setEditingHerb({...editingHerb, energetic_temp: e.target.value})}
                      className="input-acupedia mt-1 w-full"
                      data-testid="herb-temp-select"
                    >
                      <option value="cald">Cald</option>
                      <option value="rece">Rece</option>
                      <option value="neutru">Neutru</option>
                    </select>
                  </div>
                  <div>
                    <Label>Dozaj min (g)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editingHerb.dosage_min_g}
                      onChange={(e) => setEditingHerb({...editingHerb, dosage_min_g: parseFloat(e.target.value)})}
                      className="input-acupedia mt-1"
                      data-testid="herb-dosage-min-input"
                    />
                  </div>
                  <div>
                    <Label>Dozaj max (g)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={editingHerb.dosage_max_g}
                      onChange={(e) => setEditingHerb({...editingHerb, dosage_max_g: parseFloat(e.target.value)})}
                      className="input-acupedia mt-1"
                      data-testid="herb-dosage-max-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Meridiane (separate prin virgulă)</Label>
                  <Input
                    value={editingHerb.meridians?.join(', ') || ''}
                    onChange={(e) => setEditingHerb({
                      ...editingHerb, 
                      meridians: e.target.value.split(',').map(m => m.trim()).filter(m => m)
                    })}
                    placeholder="Ex: Stomac, Ficat, Plămân"
                    className="input-acupedia mt-1"
                    data-testid="herb-meridians-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHerbDialog(false)}>
                Anulează
              </Button>
              <Button
                className="btn-primary"
                onClick={async () => {
                  try {
                    if (editingHerb.id) {
                      await axios.put(`${API}/herbs/${editingHerb.id}`, editingHerb, {
                        auth: { username: 'admin', password }
                      });
                      toast.success('Plantă actualizată');
                    } else {
                      await axios.post(`${API}/herbs`, editingHerb, {
                        auth: { username: 'admin', password }
                      });
                      toast.success('Plantă adăugată');
                    }
                    setShowHerbDialog(false);
                    fetchData();
                  } catch (error) {
                    toast.error('Eroare la salvarea plantei');
                  }
                }}
                data-testid="save-herb-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                Salvează
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
